import { VisuallyHidden } from '@radix-ui/react-visually-hidden';
import { useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { DialogLogo } from './DialogLogo';
import { LegacyButton } from '~/components/ui/LegacyButton';
import { Dialog, DialogDescription, DialogTitle, type DialogProps } from '~/components/ui/LegacyDialog';
import { sendAnalyticsEvent } from '~/lib/analytics.client';
import { connectToFigma, ensureConnectionToFigma } from '~/lib/figma';
import { AnalyticsAction, AnalyticsTrackEvent } from '~/types/analytics';

export function ConnectToFigmaDialog({
  open,
  onSuccess,
  onClose,
}: {
  open: boolean;
  onSuccess: () => void;
  onClose: DialogProps['onClose'];
}) {
  return (
    <Dialog open={open} onClose={onClose} maxWidth={600}>
      {open && <ConnectToFigmaDialogInner onSuccess={onSuccess} />}
    </Dialog>
  );
}

function ConnectToFigmaDialogInner({ onSuccess }: { onSuccess: () => void }) {
  const [figmaRequest, setFigmaRequest] = useState(false);
  const abortedRef = useRef(false);

  useEffect(() => {
    return () => {
      abortedRef.current = true;
    };
  }, []);

  async function handleLogin() {
    setFigmaRequest(true);

    sendAnalyticsEvent({
      action: AnalyticsAction.Track,
      payload: {
        event: AnalyticsTrackEvent.ConnectToFigma,
      },
    });

    const connected = await ensureConnectionToFigma();

    if (!connected) {
      const success = await connectToFigma();

      if (abortedRef.current) {
        return;
      }

      if (!success) {
        toast.error('Unable to connect to Figma');
        setFigmaRequest(false);

        return;
      }
    }

    onSuccess();
  }

  return (
    <>
      <VisuallyHidden>
        <DialogTitle>Connect to Figma</DialogTitle>
      </VisuallyHidden>
      <DialogDescription noPadding>
        <DialogLogo />

        <div className="px-5">
          <h1 className="text-center text-lg font-semibold">Connect to Figma</h1>
          <div className="flex flex-col items-center w-full pt-2 pb-5 text-center">
            <span className="mb-6 max-w-80% text-bolt-elements-text-secondary">
              To get started, log in to Figma with your account or create one.
            </span>

            <div className="flex flex-col gap-4">
              <div className="flex flex-col items-center">
                <LegacyButton size="lg" variant="primary" onClick={handleLogin} disabled={figmaRequest}>
                  {figmaRequest ? 'Connecting with Figma' : 'Log in to Figma'}
                </LegacyButton>
              </div>
              {figmaRequest && (
                <span className="block text-xs text-bolt-elements-text-secondary">
                  Login with Figma in the other tab and authorize Bolt to continue
                </span>
              )}
            </div>
          </div>
        </div>
      </DialogDescription>
    </>
  );
}
